import React from 'react';
import { Link } from 'react-router-dom';
import {
    Monitor,
    Tv,
    Smartphone,
    Headphones,
    ArrowRight,
    CheckCircle2,
    Users,
    Zap,
    Clock,
    Video,
    MessageSquare,
    Laptop
} from 'lucide-react';
import PageLayout from '../../Layout/PageLayout';
import PageHeader from '../../PageHeader/PageHeader';
import ModernCTA from '../../CTA/ModernCTA';
import './ModernSolutions.scss';

const ModernWorkforce = () => {
    const services = [
        {
            icon: <Monitor size={28} />,
            title: 'Conference Room AV Systems',
            description: 'Zoom/Teams integration for seamless meetings with high-quality audio and video.',
            features: ['4K displays', 'Wireless sharing', 'Room scheduling', 'One-touch join']
        },
        {
            icon: <Tv size={28} />,
            title: 'Digital Signage Deployment',
            description: 'Dynamic displays for better communication and engagement across your organization.',
            features: ['Cloud management', 'Custom content', 'Remote updates', 'Analytics']
        },
        {
            icon: <Smartphone size={28} />,
            title: 'Mobile Device Management',
            description: 'Secure and streamlined device administration for your entire mobile fleet.',
            features: ['Device enrollment', 'App management', 'Security policies', 'Remote wipe']
        },
        {
            icon: <Headphones size={28} />,
            title: 'On-site Helpdesk Setup',
            description: 'Immediate technical support when and where you need it most.',
            features: ['Dedicated staff', 'Ticket management', 'SLA tracking', 'Knowledge base']
        },
        {
            icon: <Video size={28} />,
            title: 'Video Conferencing Solutions',
            description: 'Enterprise-grade video conferencing for remote and hybrid teams.',
            features: ['HD video', 'Screen sharing', 'Recording', 'Breakout rooms']
        },
        {
            icon: <MessageSquare size={28} />,
            title: 'Unified Communications',
            description: 'Integrate voice, video, and messaging into a single platform.',
            features: ['VoIP integration', 'Team chat', 'Presence status', 'Mobile apps']
        }
    ];

    const outcomes = [
        {
            icon: <Zap size={28} />,
            title: 'Smoother Workflows',
            description: 'Streamlined processes that eliminate bottlenecks and enhance team coordination'
        },
        {
            icon: <Clock size={28} />,
            title: 'Faster Resolution',
            description: 'Quick response times and efficient problem-solving to minimize downtime'
        },
        {
            icon: <Users size={28} />,
            title: 'Empowered Teams',
            description: 'Tools and support that enable your workforce to achieve their full potential'
        }
    ];

    const features = [
        { icon: <Laptop size={24} />, title: 'Modern Tools', text: 'Latest technology' },
        { icon: <Users size={24} />, title: 'Collaboration', text: 'Work together' },
        { icon: <Headphones size={24} />, title: 'Support', text: '24/7 helpdesk' },
        { icon: <Zap size={24} />, title: 'Productive', text: 'Work smarter' }
    ];

    return (
        <PageLayout>
            <PageHeader
                title="Workforce Experience"
                subtitle="Redefining productivity through intuitive technology"
                breadcrumbs={[
                    { label: 'Solutions', path: '/solutions' },
                    { label: 'Workforce Experience' }
                ]}
            />

            {/* Hero Section */}
            <section className="jk-solutions-hero">
                <div className="jk-solutions-hero__container">
                    <div className="jk-solutions-hero__grid">
                        <div className="jk-solutions-hero__content">
                            <span className="jk-section-label">
                                <Users size={16} />
                                Empower Your Team
                            </span>
                            <h2 className="jk-solutions-hero__title">
                                Equip Your Team with
                                <span className="highlight"> Seamless Collaboration Tools</span>
                            </h2>
                            <p className="jk-solutions-hero__description">
                                We equip your team with seamless collaboration tools and streamlined tech
                                support. From conference room systems to mobile device management, we
                                create productive work environments that drive results.
                            </p>
                            <div className="jk-solutions-hero__buttons">
                                <Link to="/contact" className="jk-btn jk-btn--primary">
                                    Transform Your Workplace
                                    <ArrowRight size={20} />
                                </Link>
                                <Link to="/service" className="jk-btn jk-btn--secondary">
                                    View Services
                                </Link>
                            </div>
                        </div>

                        <div className="jk-solutions-hero__features">
                            {features.map((feature, index) => (
                                <div key={index} className="jk-solutions-hero__feature-card">
                                    <div className="jk-solutions-hero__feature-icon">
                                        {feature.icon}
                                    </div>
                                    <h4 className="jk-solutions-hero__feature-title">{feature.title}</h4>
                                    <p className="jk-solutions-hero__feature-text">{feature.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="jk-solutions-services">
                <div className="jk-solutions-services__container">
                    <div className="jk-solutions-services__header">
                        <span className="jk-section-label">
                            <Monitor size={16} />
                            Key Offerings
                        </span>
                        <h2 className="jk-solutions-services__title">
                            Workforce Experience
                            <span className="highlight"> Solutions</span>
                        </h2>
                        <p className="jk-solutions-services__subtitle">
                            Tools and services that empower your team to work better, faster, and smarter
                        </p>
                    </div>

                    <div className="jk-solutions-services__grid">
                        {services.map((service, index) => (
                            <div key={index} className="jk-solutions-services__card">
                                <div className="jk-solutions-services__card-icon">
                                    {service.icon}
                                </div>
                                <h3 className="jk-solutions-services__card-title">{service.title}</h3>
                                <p className="jk-solutions-services__card-description">{service.description}</p>
                                <ul className="jk-solutions-services__card-features">
                                    {service.features.map((feature, idx) => (
                                        <li key={idx}>
                                            <CheckCircle2 size={12} />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Outcomes Section */}
            <section className="jk-solutions-outcomes">
                <div className="jk-solutions-outcomes__container">
                    <div className="jk-solutions-outcomes__header">
                        <h2 className="jk-solutions-outcomes__title">
                            Expected Outcomes
                        </h2>
                    </div>

                    <div className="jk-solutions-outcomes__grid">
                        {outcomes.map((outcome, index) => (
                            <div key={index} className="jk-solutions-outcomes__card">
                                <div className="jk-solutions-outcomes__card-icon">
                                    {outcome.icon}
                                </div>
                                <h3 className="jk-solutions-outcomes__card-title">{outcome.title}</h3>
                                <p className="jk-solutions-outcomes__card-text">{outcome.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <ModernCTA />
        </PageLayout>
    );
};

export default ModernWorkforce;
